export const AUTO_SIZE_BINDING_VERSION = 2;
export const AUTO_SIZE_BINDING_MIN_TEXT_PADDING = 8;

const SUPPORTED_TARGET_TYPES = new Set(['line', 'rect', 'circle', 'roundedRect', 'parallelogram']);
const BOX_TARGET_TYPES = new Set(['rect', 'roundedRect', 'parallelogram']);
const MAX_ABS_OFFSET = 4096;
const MAX_SIZE = 4096;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const roundTo = (value, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const sanitizeNumber = (value, fallback, min, max) => (
  clamp(toNumber(value, fallback), min, max)
);

const isFiniteBounds = (bounds) => (
  Boolean(bounds)
  && Number.isFinite(bounds.minX)
  && Number.isFinite(bounds.minY)
  && Number.isFinite(bounds.maxX)
  && Number.isFinite(bounds.maxY)
);

const normalizeBounds = (rawBounds) => {
  if (!rawBounds || typeof rawBounds !== 'object') return null;
  if (isFiniteBounds(rawBounds)) {
    const minX = Number(rawBounds.minX);
    const minY = Number(rawBounds.minY);
    const maxX = Number(rawBounds.maxX);
    const maxY = Number(rawBounds.maxY);
    return {
      minX,
      minY,
      maxX,
      maxY,
      width: Math.max(0, Number.isFinite(rawBounds.width) ? Number(rawBounds.width) : (maxX - minX)),
      height: Math.max(0, Number.isFinite(rawBounds.height) ? Number(rawBounds.height) : (maxY - minY)),
    };
  }

  if (
    Number.isFinite(rawBounds.x)
    && Number.isFinite(rawBounds.y)
    && Number.isFinite(rawBounds.width)
    && Number.isFinite(rawBounds.height)
  ) {
    const x = Number(rawBounds.x);
    const y = Number(rawBounds.y);
    const width = Math.max(0, Number(rawBounds.width));
    const height = Math.max(0, Number(rawBounds.height));
    return {
      minX: x,
      minY: y,
      maxX: x + width,
      maxY: y + height,
      width,
      height,
    };
  }

  return null;
};

const boundsCenter = (bounds) => ({
  x: bounds.minX + (bounds.width / 2),
  y: bounds.minY + (bounds.height / 2),
});

const validString = (value) => (
  typeof value === 'string' && value.trim() ? value.trim() : null
);

const normalizeSourceTextIds = (rawSourceTextId, rawSourceTextIds) => {
  const primary = validString(rawSourceTextId);
  const fromArray = Array.isArray(rawSourceTextIds)
    ? rawSourceTextIds.map(validString).filter(Boolean)
    : [];
  const deduped = [];
  const seen = new Set();

  const push = (value) => {
    if (!value || seen.has(value)) return;
    seen.add(value);
    deduped.push(value);
  };

  push(primary);
  fromArray.forEach(push);

  if (!deduped.length) return null;
  return {
    sourceTextId: primary || deduped[0],
    sourceTextIds: deduped,
  };
};

export const getAutoSizeBindingSourceIds = (binding) => {
  const normalized = normalizeSourceTextIds(binding?.sourceTextId, binding?.sourceTextIds);
  return normalized?.sourceTextIds || [];
};

export const bindingReferencesSourceTextId = (binding, textId) => {
  const needle = validString(textId);
  if (!needle) return false;
  return getAutoSizeBindingSourceIds(binding).includes(needle);
};

export const unionTextBounds = (boundsList) => {
  const normalized = (Array.isArray(boundsList) ? boundsList : [])
    .map((entry) => normalizeBounds(entry))
    .filter(Boolean);
  if (!normalized.length) return null;
  let minX = normalized[0].minX;
  let minY = normalized[0].minY;
  let maxX = normalized[0].maxX;
  let maxY = normalized[0].maxY;
  for (let index = 1; index < normalized.length; index += 1) {
    const bounds = normalized[index];
    minX = Math.min(minX, bounds.minX);
    minY = Math.min(minY, bounds.minY);
    maxX = Math.max(maxX, bounds.maxX);
    maxY = Math.max(maxY, bounds.maxY);
  }
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: Math.max(0, maxX - minX),
    height: Math.max(0, maxY - minY),
  };
};

const readBoundsFromMapLike = (textBoundsById, id) => {
  if (!textBoundsById || !id) return null;
  if (textBoundsById instanceof Map) return textBoundsById.get(id) || null;
  if (typeof textBoundsById === 'object') return textBoundsById[id] || null;
  return null;
};

export const resolveBindingSourceBounds = ({
  binding,
  textBoundsById,
}) => {
  const sourceTextIds = getAutoSizeBindingSourceIds(binding);
  if (!sourceTextIds.length) {
    return {
      allSourceBounds: [],
      unionBounds: null,
      primaryBounds: null,
      missingSourceIds: [],
      validSourceIds: [],
    };
  }

  const allSourceBounds = [];
  const missingSourceIds = [];
  const validSourceIds = [];
  sourceTextIds.forEach((sourceId) => {
    const bounds = normalizeBounds(readBoundsFromMapLike(textBoundsById, sourceId));
    if (!bounds) {
      missingSourceIds.push(sourceId);
      return;
    }
    allSourceBounds.push({ id: sourceId, bounds });
    validSourceIds.push(sourceId);
  });

  const unionBoundsValue = unionTextBounds(allSourceBounds.map((entry) => entry.bounds));
  const primaryId = validString(binding?.sourceTextId) || sourceTextIds[0] || null;
  const primaryBounds = normalizeBounds(readBoundsFromMapLike(textBoundsById, primaryId))
    || allSourceBounds[0]?.bounds
    || null;

  return {
    allSourceBounds,
    unionBounds: unionBoundsValue,
    primaryBounds,
    missingSourceIds,
    validSourceIds,
  };
};

const adjustAxisPadsForAnchoredTextMove = ({
  padMin,
  padMax,
  delta,
  anchor = 'min',
}) => {
  const safePadMin = Math.max(0, toNumber(padMin, 0));
  const safePadMax = Math.max(0, toNumber(padMax, 0));
  const safeDelta = toNumber(delta, 0);

  if (anchor === 'max') {
    // Moving toward min (negative delta) keeps max edge fixed and consumes min-side slack first.
    if (safeDelta <= 0) {
      return {
        padMin: roundTo(Math.max(0, safePadMin + safeDelta), 2),
        padMax: roundTo(safePadMax - safeDelta, 2),
      };
    }

    // Moving toward max retracts prior min-side expansions by shrinking max pad first.
    if (safeDelta <= safePadMax) {
      return {
        padMin: roundTo(safePadMin, 2),
        padMax: roundTo(Math.max(0, safePadMax - safeDelta), 2),
      };
    }

    // Once max pad is exhausted, switch to expanding the max side (min edge fixed).
    const residual = safeDelta - safePadMax;
    return {
      padMin: roundTo(safePadMin + residual, 2),
      padMax: 0,
    };
  }

  // anchor === 'min'
  // Moving toward max (positive delta) keeps min edge fixed and consumes max-side slack first.
  if (safeDelta >= 0) {
    return {
      padMin: roundTo(safePadMin + safeDelta, 2),
      padMax: roundTo(Math.max(0, safePadMax - safeDelta), 2),
    };
  }

  const magnitude = Math.abs(safeDelta);
  // Moving toward min retracts prior max-side expansions by shrinking min pad first.
  if (magnitude <= safePadMin) {
    return {
      padMin: roundTo(Math.max(0, safePadMin - magnitude), 2),
      padMax: roundTo(safePadMax, 2),
    };
  }

  // Once min pad is exhausted, switch to expanding the min side (max edge fixed).
  const residual = magnitude - safePadMin;
  return {
    padMin: 0,
    padMax: roundTo(safePadMax + residual, 2),
  };
};

export const inferAutoSizeBindingModeForShape = (shape) => {
  if (!shape) return null;
  const type = shape.type;
  if (type === 'line') return 'line';
  if (type === 'circle') return 'circle';
  if (BOX_TARGET_TYPES.has(type)) return 'box';
  return null;
};

export const isAutoSizeBindingTarget = (shape) => {
  if (!shape || typeof shape !== 'object') return false;
  const layerKind = shape.layerKind || 'shape';
  if (layerKind !== 'shape') return false;
  return SUPPORTED_TARGET_TYPES.has(shape.type);
};

export const isAutoSizeBindingSource = (shape) => (
  Boolean(shape)
  && typeof shape === 'object'
  && shape.layerKind === 'text'
);

const sanitizeBoxPayload = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    padLeft: sanitizeNumber(raw.padLeft, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    padRight: sanitizeNumber(raw.padRight, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    padTop: sanitizeNumber(raw.padTop, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    padBottom: sanitizeNumber(raw.padBottom, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    minWidth: sanitizeNumber(raw.minWidth, 1, 1, MAX_SIZE),
    minHeight: sanitizeNumber(raw.minHeight, 1, 1, MAX_SIZE),
  };
};

const sanitizeLinePayload = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    padLeft: sanitizeNumber(raw.padLeft, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    padRight: sanitizeNumber(raw.padRight, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    centerOffsetY: sanitizeNumber(raw.centerOffsetY, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    minWidth: sanitizeNumber(raw.minWidth, 1, 1, MAX_SIZE),
    fixedHeight: sanitizeNumber(raw.fixedHeight, 1, 1, MAX_SIZE),
  };
};

const sanitizeCirclePayload = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  return {
    centerOffsetX: sanitizeNumber(raw.centerOffsetX, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    centerOffsetY: sanitizeNumber(raw.centerOffsetY, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    paddingX: sanitizeNumber(raw.paddingX, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    paddingY: sanitizeNumber(raw.paddingY, 0, -MAX_ABS_OFFSET, MAX_ABS_OFFSET),
    minDiameter: sanitizeNumber(raw.minDiameter, 1, 1, MAX_SIZE),
  };
};

export const sanitizeAutoSizeBinding = (raw, shape) => {
  if (!isAutoSizeBindingTarget(shape)) return null;
  if (!raw || typeof raw !== 'object') return null;

  const normalizedSources = normalizeSourceTextIds(raw.sourceTextId, raw.sourceTextIds);
  if (!normalizedSources) return null;

  const mode = inferAutoSizeBindingModeForShape(shape);
  if (!mode) return null;

  const base = {
    version: AUTO_SIZE_BINDING_VERSION,
    enabled: raw.enabled !== false,
    sourceTextId: normalizedSources.sourceTextId,
    sourceTextIds: normalizedSources.sourceTextIds,
    mode,
  };

  if (mode === 'line') {
    const line = sanitizeLinePayload(raw.line);
    if (!line) return null;
    return { ...base, line };
  }
  if (mode === 'circle') {
    const circle = sanitizeCirclePayload(raw.circle);
    if (!circle) return null;
    return { ...base, circle };
  }
  const box = sanitizeBoxPayload(raw.box);
  if (!box) return null;
  return { ...base, box };
};

const getBoxTargetBounds = (shape, targetGeometry) => {
  const fromGeometry = normalizeBounds(targetGeometry?.baseBounds || targetGeometry?.bounds || null);
  if (fromGeometry) return fromGeometry;
  return normalizeBounds({
    x: toNumber(shape?.x, 0),
    y: toNumber(shape?.y, 0),
    width: Math.max(1, toNumber(shape?.width, 1)),
    height: Math.max(1, toNumber(shape?.height, 1)),
  });
};

const getLineGeometry = (shape) => ({
  x: toNumber(shape?.x, 0),
  y: toNumber(shape?.y, 0),
  width: Math.max(1, toNumber(shape?.width, 1)),
  height: Math.max(1, toNumber(shape?.height, 1)),
  strokeWidth: Math.max(1, toNumber(shape?.strokeWidth, 1)),
});

const getCircleGeometry = (shape) => {
  const size = Math.max(1, toNumber(shape?.width ?? shape?.height, 1));
  return {
    x: toNumber(shape?.x, 0),
    y: toNumber(shape?.y, 0),
    size,
  };
};

export const captureAutoSizeBinding = ({
  sourceTextBounds,
  primarySourceTextBounds = null,
  targetPrimitive,
  targetGeometry = null,
  sourceTextId = null,
  sourceTextIds = null,
}) => {
  if (!isAutoSizeBindingTarget(targetPrimitive)) return null;
  const textBounds = normalizeBounds(sourceTextBounds);
  const primaryTextBounds = normalizeBounds(primarySourceTextBounds || sourceTextBounds);
  if (!textBounds) return null;

  const mode = inferAutoSizeBindingModeForShape(targetPrimitive);
  if (!mode) return null;

  const normalizedSources = normalizeSourceTextIds(
    sourceTextId
      || targetPrimitive?.autoSizeBinding?.sourceTextId
      || null,
    sourceTextIds
      || targetPrimitive?.autoSizeBinding?.sourceTextIds
      || null,
  );
  if (!normalizedSources) return null;

  const base = {
    version: AUTO_SIZE_BINDING_VERSION,
    enabled: true,
    sourceTextId: normalizedSources.sourceTextId,
    sourceTextIds: normalizedSources.sourceTextIds,
    mode,
  };

  if (mode === 'line') {
    const line = getLineGeometry(targetPrimitive);
    const textCenter = boundsCenter(primaryTextBounds || textBounds);
    return {
      ...base,
      line: {
        padLeft: roundTo(Math.max(0, textBounds.minX - line.x), 2),
        padRight: roundTo(Math.max(0, (line.x + line.width) - textBounds.maxX), 2),
        centerOffsetY: roundTo(line.y - textCenter.y, 2),
        minWidth: roundTo(Math.max(line.width, textBounds.width, 1), 2),
        fixedHeight: roundTo(Math.max(line.height, line.strokeWidth), 2),
      },
    };
  }

  if (mode === 'circle') {
    const circle = getCircleGeometry(targetPrimitive);
    const textCenter = boundsCenter(textBounds);
    const circleCenter = {
      x: circle.x + (circle.size / 2),
      y: circle.y + (circle.size / 2),
    };
    return {
      ...base,
      circle: {
        centerOffsetX: roundTo(circleCenter.x - textCenter.x, 2),
        centerOffsetY: roundTo(circleCenter.y - textCenter.y, 2),
        paddingX: roundTo(Math.max(0, (circle.size - textBounds.width) / 2), 2),
        paddingY: roundTo(Math.max(0, (circle.size - textBounds.height) / 2), 2),
        minDiameter: roundTo(Math.max(circle.size, textBounds.width, textBounds.height, 1), 2),
      },
    };
  }

  const targetBounds = getBoxTargetBounds(targetPrimitive, targetGeometry);
  if (!targetBounds) return null;
  return {
    ...base,
    box: {
      padLeft: roundTo(Math.max(0, textBounds.minX - targetBounds.minX), 2),
      padRight: roundTo(Math.max(0, targetBounds.maxX - textBounds.maxX), 2),
      padTop: roundTo(Math.max(0, textBounds.minY - targetBounds.minY), 2),
      padBottom: roundTo(Math.max(0, targetBounds.maxY - textBounds.maxY), 2),
      minWidth: roundTo(Math.max(1, targetBounds.width, textBounds.width), 2),
      minHeight: roundTo(Math.max(1, targetBounds.height, textBounds.height), 2),
    },
  };
};

export const recalibrateAutoSizeBinding = ({
  shape,
  binding,
  sourceTextBounds,
  primarySourceTextBounds = null,
  targetGeometry = null,
}) => {
  const sanitized = sanitizeAutoSizeBinding(binding, shape);
  if (!sanitized) return null;
  const captured = captureAutoSizeBinding({
    sourceTextBounds,
    primarySourceTextBounds,
    targetPrimitive: { ...shape, autoSizeBinding: sanitized },
    targetGeometry,
    sourceTextId: sanitized.sourceTextId,
    sourceTextIds: sanitized.sourceTextIds,
  });
  if (!captured) return null;
  return {
    ...captured,
    enabled: sanitized.enabled !== false,
  };
};

export const adjustAutoSizeBindingForTextMove = ({
  shape,
  binding,
  startTextBounds,
  nextTextBounds,
  anchors = null,
  referenceTargetBounds = null,
  referenceBinding = null,
}) => {
  const sanitized = sanitizeAutoSizeBinding(binding, shape);
  const startBounds = normalizeBounds(startTextBounds);
  const endBounds = normalizeBounds(nextTextBounds);
  if (!sanitized || !startBounds || !endBounds) return sanitized;
  const referenceBounds = normalizeBounds(referenceTargetBounds);
  const referenceSanitized = sanitizeAutoSizeBinding(referenceBinding || binding, shape);

  const startCenter = boundsCenter(startBounds);
  const endCenter = boundsCenter(endBounds);
  const deltaMinX = endBounds.minX - startBounds.minX;
  const deltaMinY = endBounds.minY - startBounds.minY;
  const deltaCenterX = endCenter.x - startCenter.x;
  const deltaCenterY = endCenter.y - startCenter.y;

  if (sanitized.mode === 'circle') {
    const nextBinding = {
      ...sanitized,
      circle: {
        ...sanitized.circle,
        centerOffsetX: roundTo(sanitized.circle.centerOffsetX - deltaCenterX, 2),
        centerOffsetY: roundTo(sanitized.circle.centerOffsetY - deltaCenterY, 2),
      },
    };
    return sanitizeAutoSizeBinding(nextBinding, shape);
  }

  if (sanitized.mode === 'line') {
    if (referenceBounds) {
      const refLine = referenceSanitized?.line || sanitized.line;
      const unionMinX = Math.min(referenceBounds.minX, endBounds.minX);
      const unionMaxX = Math.max(referenceBounds.maxX, endBounds.maxX);
      const nextBinding = {
        ...sanitized,
        line: {
          ...sanitized.line,
          centerOffsetY: roundTo(refLine?.centerOffsetY ?? sanitized.line.centerOffsetY, 2),
          fixedHeight: roundTo(refLine?.fixedHeight ?? sanitized.line.fixedHeight, 2),
          minWidth: roundTo(Math.max(1, referenceBounds.width), 2),
          padLeft: roundTo(endBounds.minX - unionMinX, 2),
          padRight: roundTo(unionMaxX - endBounds.maxX, 2),
        },
      };
      return sanitizeAutoSizeBinding(nextBinding, shape);
    }
    const xAnchor = anchors?.x || (deltaMinX >= 0 ? 'left' : 'right');
    const adjustedX = adjustAxisPadsForAnchoredTextMove({
      padMin: sanitized.line.padLeft,
      padMax: sanitized.line.padRight,
      delta: deltaMinX,
      anchor: xAnchor === 'right' ? 'max' : 'min',
    });
    const nextBinding = {
      ...sanitized,
      line: {
        ...sanitized.line,
        padLeft: adjustedX.padMin,
        padRight: adjustedX.padMax,
      },
    };
    return sanitizeAutoSizeBinding(nextBinding, shape);
  }

  if (referenceBounds) {
    const unionMinX = Math.min(referenceBounds.minX, endBounds.minX);
    const unionMinY = Math.min(referenceBounds.minY, endBounds.minY);
    const unionMaxX = Math.max(referenceBounds.maxX, endBounds.maxX);
    const unionMaxY = Math.max(referenceBounds.maxY, endBounds.maxY);
    const nextBinding = {
      ...sanitized,
      box: {
        ...sanitized.box,
        minWidth: roundTo(Math.max(1, referenceBounds.width), 2),
        minHeight: roundTo(Math.max(1, referenceBounds.height), 2),
        padLeft: roundTo(endBounds.minX - unionMinX, 2),
        padRight: roundTo(unionMaxX - endBounds.maxX, 2),
        padTop: roundTo(endBounds.minY - unionMinY, 2),
        padBottom: roundTo(unionMaxY - endBounds.maxY, 2),
      },
    };
    return sanitizeAutoSizeBinding(nextBinding, shape);
  }

  const xAnchor = anchors?.x || (deltaMinX >= 0 ? 'left' : 'right');
  const yAnchor = anchors?.y || (deltaMinY >= 0 ? 'top' : 'bottom');
  const adjustedX = adjustAxisPadsForAnchoredTextMove({
    padMin: sanitized.box.padLeft,
    padMax: sanitized.box.padRight,
    delta: deltaMinX,
    anchor: xAnchor === 'right' ? 'max' : 'min',
  });
  const adjustedY = adjustAxisPadsForAnchoredTextMove({
    padMin: sanitized.box.padTop,
    padMax: sanitized.box.padBottom,
    delta: deltaMinY,
    anchor: yAnchor === 'bottom' ? 'max' : 'min',
  });
  const nextBinding = {
    ...sanitized,
    box: {
      ...sanitized.box,
      padLeft: adjustedX.padMin,
      padRight: adjustedX.padMax,
      padTop: adjustedY.padMin,
      padBottom: adjustedY.padMax,
    },
  };
  return sanitizeAutoSizeBinding(nextBinding, shape);
};

const applyBoxBinding = (shape, binding, textBounds) => {
  const payload = binding.box;
  if (!payload) return shape;
  const padLeft = Math.max(AUTO_SIZE_BINDING_MIN_TEXT_PADDING, toNumber(payload.padLeft, 0));
  const padRight = Math.max(AUTO_SIZE_BINDING_MIN_TEXT_PADDING, toNumber(payload.padRight, 0));
  const padTop = Math.max(AUTO_SIZE_BINDING_MIN_TEXT_PADDING, toNumber(payload.padTop, 0));
  const padBottom = Math.max(AUTO_SIZE_BINDING_MIN_TEXT_PADDING, toNumber(payload.padBottom, 0));
  const x = textBounds.minX - padLeft;
  const y = textBounds.minY - padTop;
  const width = Math.max(payload.minWidth, textBounds.width + padLeft + padRight);
  const height = Math.max(payload.minHeight, textBounds.height + padTop + padBottom);
  return {
    ...shape,
    x: roundTo(x, 2),
    y: roundTo(y, 2),
    width: roundTo(Math.max(1, width), 2),
    height: roundTo(Math.max(1, height), 2),
    autoSizeBinding: binding,
  };
};

const applyLineBinding = (shape, binding, textBounds, primaryTextBounds = null) => {
  const payload = binding.line;
  if (!payload) return shape;
  const yAnchorBounds = normalizeBounds(primaryTextBounds || textBounds) || textBounds;
  const textCenter = boundsCenter(yAnchorBounds);
  const strokeWidth = Math.max(1, toNumber(shape?.strokeWidth, 1));
  const width = Math.max(payload.minWidth, textBounds.width + payload.padLeft + payload.padRight);
  const y = textCenter.y + payload.centerOffsetY;
  const height = Math.max(strokeWidth, payload.fixedHeight);
  return {
    ...shape,
    x: roundTo(textBounds.minX - payload.padLeft, 2),
    y: roundTo(y, 2),
    width: roundTo(Math.max(1, width), 2),
    height: roundTo(Math.max(1, height), 2),
    autoSizeBinding: binding,
  };
};

const applyCircleBinding = (shape, binding, textBounds) => {
  const payload = binding.circle;
  if (!payload) return shape;
  const paddingX = Math.max(AUTO_SIZE_BINDING_MIN_TEXT_PADDING, toNumber(payload.paddingX, 0));
  const paddingY = Math.max(AUTO_SIZE_BINDING_MIN_TEXT_PADDING, toNumber(payload.paddingY, 0));
  const textCenter = boundsCenter(textBounds);
  const centerX = textCenter.x + payload.centerOffsetX;
  const centerY = textCenter.y + payload.centerOffsetY;
  const halfWidthFromCenter = Math.max(
    centerX - textBounds.minX,
    textBounds.maxX - centerX,
    0,
  );
  const halfHeightFromCenter = Math.max(
    centerY - textBounds.minY,
    textBounds.maxY - centerY,
    0,
  );
  const diameter = Math.max(
    payload.minDiameter,
    (halfWidthFromCenter + paddingX) * 2,
    (halfHeightFromCenter + paddingY) * 2,
    1,
  );
  return {
    ...shape,
    x: roundTo(centerX - (diameter / 2), 2),
    y: roundTo(centerY - (diameter / 2), 2),
    width: roundTo(diameter, 2),
    height: roundTo(diameter, 2),
    autoSizeBinding: binding,
  };
};

export const applyAutoSizeBindingToShape = ({
  shape,
  binding,
  sourceTextBounds,
  primarySourceTextBounds = null,
}) => {
  const sanitized = sanitizeAutoSizeBinding(binding, shape);
  const textBounds = normalizeBounds(sourceTextBounds);
  if (!sanitized || !textBounds || sanitized.enabled === false) return shape;

  if (sanitized.mode === 'line') return applyLineBinding(shape, sanitized, textBounds, primarySourceTextBounds);
  if (sanitized.mode === 'circle') return applyCircleBinding(shape, sanitized, textBounds);
  return applyBoxBinding(shape, sanitized, textBounds);
};
